import { Location, LocationMapper } from '@nx-smartmonkey/shared/domain';

import { FieldMapping } from '../../../../../../components/data-importer/fields/FieldMapping';
import { StopDTO } from '../../../../../../domain/stop/StopDTO';
import { TaskDTO } from '../../../../../../domain/task/TaskDTO';

export class CreateStopsList {
  constructor(
    private stopsData: StopDTO[],
    private stopsAddresses: string[],
    private pickupsAddresses?: string[],
    private isUpdated?: boolean[]
  ) {}

  getStopByIndex(index: number) {
    return this.stopsData[index];
  }

  getStopLabel(stopId: string) {
    const stop = this.stopsData.find((stop) => stop.id === stopId);
    return stop?.label;
  }

  getStopsAddresses() {
    return this.stopsAddresses;
  }

  getStopAddress(stopId: string) {
    const index = this.stopsData.findIndex((stop) => stop.id === stopId);
    return this.stopsAddresses[index];
  }

  getStopLocation(stopId: string) {
    const stop = this.stopsData.find((stop) => stop.id === stopId);
    return stop?.location ? LocationMapper.toDomain(stop.location) : null;
  }

  getIsUpdatedById(stopId: string) {
    const index = this.stopsData.findIndex((stop) => stop.id === stopId);
    return this.isUpdated && this.isUpdated[index];
  }

  setStopLocation(stopId: string, location?: Location | null) {
    const stop = this.stopsData.find((stop) => stop.id === stopId);
    const index = this.stopsData.findIndex((stop) => stop.id === stopId);
    if (stop && location) {
      stop.location = LocationMapper.toDTO(location);
      this.isUpdated![index] = true;
    }
    return new CreateStopsList(this.stopsData, this.stopsAddresses, this.pickupsAddresses, this.isUpdated);
  }

  setStopsAddresses(locations: (Location | null)[]) {
    if (locations.length !== this.stopsAddresses.length) {
      throw new Error(`The number of addresses does not match the number of stops`);
    }

    this.stopsData.forEach((stop, index) => {
      stop.location = locations[index] ?? undefined;
      this.isUpdated![index] = false;
    });

    return new CreateStopsList(this.stopsData, this.stopsAddresses, this.pickupsAddresses, this.isUpdated);
  }

  hasPickups() {
    return this.stopsData.some((stop) => stop.pickup);
  }

  getPickups() {
    return this.stopsData.map((stop) => stop.pickup);
  }

  getPickupLabel(pickupId: string) {
    const pickup = this.stopsData.find((stop) => stop.pickup?.id === pickupId);
    return pickup?.pickup?.label;
  }

  getPickupsAddresses() {
    return this.pickupsAddresses;
  }

  getPickupAddress(pickupId: string) {
    const index = this.stopsData.findIndex((stop) => stop.pickup?.id === pickupId);
    return this.pickupsAddresses![index];
  }

  getPickupLocation(pickupId: string) {
    const pickup = this.stopsData.find((stop) => stop.pickup?.id === pickupId);
    return pickup?.pickup?.location ? LocationMapper.toDomain(pickup.pickup.location) : null;
  }

  setPickupLocation(pickupId: string, location?: Location | null) {
    const pickup = this.stopsData.find((stop) => stop.pickup?.id === pickupId);
    const index = this.stopsData.findIndex((stop) => stop.pickup?.id === pickupId);
    if (pickup && location) {
      pickup.pickup!.location = LocationMapper.toDTO(location);
      this.isUpdated![index] = true;
    }
    return new CreateStopsList(this.stopsData, this.stopsAddresses, this.pickupsAddresses, this.isUpdated);
  }

  setPickupsAddresses(locations: (Location | null)[]) {
    if (locations.length !== this.pickupsAddresses?.length) {
      throw new Error(`The number of addresses does not match the number of pickups`);
    }

    this.stopsData.forEach((stop, index) => {
      if (this.stopsData[index].pickup) {
        this.stopsData[index].pickup!.location = locations[index] ?? undefined;
        this.isUpdated![index] = false;
      }
    });

    return new CreateStopsList(this.stopsData, this.stopsAddresses, this.pickupsAddresses, this.isUpdated);
  }

  getStops(): StopDTO[] {
    return this.stopsData;
  }

  static create(
    stopsMapping: FieldMapping,
    pickupsMapping?: FieldMapping,
    tasksMapping?: FieldMapping,
    isUpdated?: boolean[]
  ) {
    let stops = stopsMapping.getData().map(({ id, ...stop }) => ({ id: `${id}`, ...stop })) as StopDTO[];
    const stopsAddresses = stopsMapping.getAddresses();

    let pickupsAddresses: string[] | undefined;

    if (pickupsMapping) {
      const pickups = pickupsMapping.getData().map(({ id, ...stop }) => ({ id: `${id}`, ...stop })) as StopDTO[];
      pickupsAddresses = pickupsMapping.getAddresses();
      stops.forEach((stop, idx) => {
        if (pickupsAddresses![idx] !== `` && Object.keys(pickups[idx]).length > 1) {
          stop.pickup = pickups[idx];
        }
      });
    }

    if (tasksMapping) {
      // If there are tasks, map them to the stops by the external_id.
      // One stop may have multiple tasks
      const tasksData = tasksMapping.getData();

      const stopExternalIdMap: { [key: string]: TaskDTO[] } = {};
      tasksData.forEach((taskData) => {
        const { external_id, ...task } = taskData;
        if (external_id) {
          if (!stopExternalIdMap[external_id]) {
            stopExternalIdMap[external_id] = [];
          }
          stopExternalIdMap[external_id].push(task as TaskDTO);
        }
      });

      // Filter the stops that does not have external_id or the first one with the same external_id.
      stops = stops.reduce((acc, stop, index) => {
        if (!stop.external_id || !acc.find((s: any) => s.external_id === stop.external_id)) {
          return [...acc, stop];
        }
        // Remove stop address because of the wrapped tasks
        stopsAddresses.splice(index, 1);

        // Remove pickup address because of the wrapped tasks
        if (pickupsAddresses) {
          pickupsAddresses.splice(index, 1);
        }
        return acc;
      }, [] as StopDTO[]);

      stops = stops.map((stop) => {
        const tasks = stopExternalIdMap[stop.external_id!];
        if (tasks) {
          stop.tasks = tasks.splice(1, tasks.length);
        }
        return stop;
      });
    }
    const isUpdatedList = isUpdated ?? stops.map(() => false);

    return new CreateStopsList(stops, stopsAddresses, pickupsAddresses, isUpdatedList);
  }
}
